import React from 'react';
import Select from 'react-select';
import {useQuery, useQueryClient} from 'react-query';
import {SmartInput} from '../../../../../components/SmartInput';
import {useCustomLists} from '../../../../../hooks/useCustomLists';
import {fetchACustomList} from '../../../../../requests/queries/flows';
import {IS_TRUE_OR_FALSE} from '../../EditProcess/transactions';
import {customStyles, DropdownIndicator} from '../../../../../components';
import {useFlowData} from '../../EditProcess/hooks/useFlowData';
import {useRuleLists} from '../hooks/useRuleLists';
import {upsertByConditionId} from '../hooks/ruleUtils';

const OPERATOR_TYPES = {
  IN: 'in',
  NOT_IN: 'notIn',
  CONTAINS: 'contains',
  DOES_NOT_CONTAIN: 'doesNotContain',
  BOOLEAN: 'boolean',
};

/**
 * ConditionValueInput intelligently renders the correct input
 * based on the condition's operator and data type.
 */
export const ConditionValueInput = ({
  condition,
  operator,
  onValueChange,
  ruleId,
}) => {
  const {customLists, isLoading: customListLoading} = useCustomLists();
  const [selectedList, setSelectedList] = React.useState(null);
  const FLowData = useFlowData();
  const queryClient = useQueryClient();

  const {currentList, ruleLists} = useRuleLists({
    ruleId: ruleId,
    value: condition?.value,
    conditionId: condition?.id,
  });

  useQuery(
    ['customList', selectedList?.value],
    () => fetchACustomList(selectedList.value),
    {
      enabled: !!selectedList?.value,
      onSuccess: async data => {
        if (data?.items) {
          onValueChange('value', data.items);
          if (ruleId) {
            await FLowData.saveRuleListsAssociation(
              {
                rule: ruleId,
                list: upsertByConditionId(ruleLists, {
                  id: selectedList?.value,
                  condition: condition?.id,
                  name: data?.name,
                }),
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries('ruleLists');
                },
              },
            );
          }
        }
      },
    },
  );

  const operatorValue = operator?.value;
  const isBooleanType =
    operator?.operatorType === OPERATOR_TYPES.BOOLEAN ||
    ['true', 'false'].includes(String(condition.value));
  const isArrayFromOperator = Array.isArray(operator?.example);
  const isListOperator = [
    OPERATOR_TYPES.IN,
    OPERATOR_TYPES.NOT_IN,
    OPERATOR_TYPES.CONTAINS,
    OPERATOR_TYPES.DOES_NOT_CONTAIN,
  ].includes(operatorValue);

  if (isBooleanType) {
    return (
      <Select
        options={IS_TRUE_OR_FALSE}
        onChange={option => onValueChange('value', String(option?.value ?? ''))}
        value={IS_TRUE_OR_FALSE.find(
          opt => String(opt.value) === String(condition.value),
        )}
        placeholder="Select"
        isClearable
        classNamePrefix="react-select"
        className="basic-multi-select white-select capitalize"
        styles={customStyles}
        components={{DropdownIndicator}}
      />
    );
  }

  if (isArrayFromOperator) {
    const options = operator.example.map(d => ({label: d, value: d}));
    return (
      <Select
        options={options}
        onChange={option => onValueChange('value', option?.value ?? '')}
        value={options.find(opt => opt.value === condition.value)}
        placeholder="Select"
        isClearable
        classNamePrefix="react-select"
        className="basic-multi-select white-select capitalize"
        styles={customStyles}
        components={{DropdownIndicator}}
      />
    );
  }

  if (isListOperator) {
    return (
      <Select
        options={customLists}
        isLoading={customListLoading}
        onChange={setSelectedList}
        value={
          selectedList || {
            ...(condition.id === currentList?.condition && {
              label: currentList?.name,
              value: currentList?.id,
            }),
          }
        }
        placeholder={customListLoading ? '' : 'Select a list'}
        isClearable
        components={{DropdownIndicator}}
        classNamePrefix="react-select"
        className="basic-multi-select white-select capitalize"
        styles={customStyles}
      />
    );
  }
  return (
    <SmartInput
      value={!Array.isArray(condition?.value) ? condition?.value : ''}
      onChange={value => onValueChange('value', value)}
      type={operator?.operatorType || 'text'}
      className="text-sm text-body"
    />
  );
};
